import { BrButton, BrDivider, BrLoading, BrSelect, Col, Row } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import AnmSwitch from 'components/anmSwitch/anmSwitch';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { useNotificacao } from 'hooks/useNotificacao';
import { IEstrutura, IFerrovia, IFerroviasLista, IOperacaoPortuaria, IPortosLista } from 'interfaces/IAfetacoes';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IMunicipio } from 'interfaces/IMunicipio';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import React, { useContext, useEffect } from 'react';
import { Table } from 'react-dsgov';
import { Tooltip } from 'react-tooltip';
import { estadosUFOptions } from 'utils/estadosUFLista';
import styles from './modalidadeAfetacaoForm.module.scss';

interface IModalidadeAfetacaoProps {
  ferroviasAdicionadas: IFerrovia[];
  setFerroviasAdicionadas: React.Dispatch<React.SetStateAction<IFerrovia[]>>;
  operacoesPortuariasAdicionadas: IOperacaoPortuaria[];
  setOperacoesPortuariasAdicionadas: React.Dispatch<React.SetStateAction<IOperacaoPortuaria[]>>;
  estruturasAdicionadas: IEstrutura[];
  setEstruturasAdicionadas: React.Dispatch<React.SetStateAction<IEstrutura[]>>;
  hasFerrovia: boolean;
  setHasFerrovia: React.Dispatch<React.SetStateAction<boolean>>;
  hasDutovia: boolean;
  setHasDutovia: React.Dispatch<React.SetStateAction<boolean>>;
  hasOperacoesPortuarias: boolean;
  setHasOperacoesPortuarias: React.Dispatch<React.SetStateAction<boolean>>;
  hasEstruturasFora: boolean;
  setHasEstruturasFora: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModalidadeAfetacao = ({
  ferroviasAdicionadas,
  setFerroviasAdicionadas,
  operacoesPortuariasAdicionadas,
  setOperacoesPortuariasAdicionadas,
  estruturasAdicionadas,
  setEstruturasAdicionadas,
  hasFerrovia,
  setHasFerrovia,
  hasDutovia,
  setHasDutovia,
  hasOperacoesPortuarias,
  setHasOperacoesPortuarias,
  hasEstruturasFora,
  setHasEstruturasFora,
}: IModalidadeAfetacaoProps) => {
  const [ferrovia, setFerrovia] = React.useState(-1);
  const [ferroviaNome, setFerroviaNome] = React.useState('');
  const [municipioDestinoFerrovia, setMunicipioDestinoFerrovia] = React.useState(-1);
  const [municipioNomeDestinoFerrovia, setMunicipioNomeDestinoFerrovia] = React.useState('');
  const [municipioOrigemFerrovia, setMunicipioOrigemFerrovia] = React.useState(-1);
  const [municipioNomeOrigemFerrovia, setMunicipioNomeOrigemFerrovia] = React.useState('');
  const [ufDestinoFerrovia, setUfDestinoFerrovia] = React.useState('');
  const [ufOrigemFerrovia, setUfOrigemFerrovia] = React.useState('');
  const [estadosUfFerroviaOptions, setEstadosUfFerroviaOptions] = React.useState([{ label: '', value: '' }]);
  const [ferroviaData, setFerroviaData] = React.useState<IFerroviasLista[]>([]);

  const [idPortoOperacao, setIdPortoOperacao] = React.useState(-1);
  const [cidadePortoOperacao, setCidadePortoOperacao] = React.useState('');

  const [ufEstruturaFora, setUfEstruturaFora] = React.useState('');
  const [municipioEstruturaFora, setMunicipioEstruturaFora] = React.useState(-1);
  const [municipioNomeEstruturaFora, setMunicipioNomeEstruturaFora] = React.useState('');

  const { getFerrovias, getMunicipiosPorUf, getMunicipiosPorFerroviaEUf, getPortos, getCanHaveDutovia } =
    useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const context = useContext(ArrecadacaoContext);

  useEffect(() => {
    if (ferroviaData && ferrovia !== -1) {
      const ufsSet = new Set<string>();
      ferroviaData
        .filter((f) => f.id === Number(ferrovia))
        .forEach((ferroviaFiltrada) => {
          ferroviaFiltrada.ufs.forEach((uf) => ufsSet.add(uf));
        });
      setEstadosUfFerroviaOptions(Array.from(ufsSet).map((uf) => ({ label: uf, value: uf })));
      setUfDestinoFerrovia('');
      setUfOrigemFerrovia('');
      return;
    }
    setEstadosUfFerroviaOptions([]);
  }, [ferroviaData, ferrovia]);

  const adicionarFerrovia = () => {
    if (!ferrovia || !ufOrigemFerrovia || !municipioOrigemFerrovia || !ufDestinoFerrovia || !municipioDestinoFerrovia) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, preencha todos os campos para adicionar uma ferrovia.',
      });
      return;
    }
    if (ufOrigemFerrovia === ufDestinoFerrovia && municipioOrigemFerrovia === municipioDestinoFerrovia) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'A origem e o destino não podem ser iguais.',
      });
      return;
    }

    const novaFerrovia: IFerrovia = {
      idFerrovia: ferrovia,
      nomeFerrovia: ferroviaNome,
      origem: {
        uf: ufOrigemFerrovia,
        codigoIBGE: municipioOrigemFerrovia,
        nome: municipioNomeOrigemFerrovia,
      },
      destino: {
        uf: ufDestinoFerrovia,
        codigoIBGE: municipioDestinoFerrovia,
        nome: municipioNomeDestinoFerrovia,
      },
    };

    const jaExiste = ferroviasAdicionadas.some(
      (d) =>
        d?.idFerrovia?.toString() === novaFerrovia?.idFerrovia?.toString() &&
        d?.origem?.uf?.toString() === novaFerrovia?.origem?.uf?.toString() &&
        d?.origem?.codigoIBGE?.toString() === novaFerrovia?.origem?.codigoIBGE?.toString() &&
        d?.destino?.uf?.toString() === novaFerrovia?.destino?.uf?.toString() &&
        d?.destino?.codigoIBGE?.toString() === novaFerrovia?.destino?.codigoIBGE?.toString()
    );
    if (jaExiste) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Esta ferrovia já foi adicionada.',
      });
      return;
    }

    setFerroviasAdicionadas([...ferroviasAdicionadas, novaFerrovia]);
    setFerrovia(-1);
    setUfOrigemFerrovia('');
    setMunicipioOrigemFerrovia(-1);
    setUfDestinoFerrovia('');
    setMunicipioDestinoFerrovia(-1);
  };

  const adicionarOperacaoPortuaria = () => {
    if (!idPortoOperacao || !cidadePortoOperacao) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, preencha todos os campos para adicionar uma operação portuária.',
      });
      return;
    }

    const novaOperacaoPortuaria: IOperacaoPortuaria = {
      idPorto: idPortoOperacao,
      municipio: cidadePortoOperacao,
    };

    const jaExiste = operacoesPortuariasAdicionadas.some(
      (e) =>
        e?.idPorto?.toString() === novaOperacaoPortuaria?.idPorto?.toString() &&
        e?.municipio?.toString() === novaOperacaoPortuaria?.municipio?.toString()
    );
    if (jaExiste) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Esta operação portuária já foi adicionada.',
      });
      return;
    }

    setOperacoesPortuariasAdicionadas([...operacoesPortuariasAdicionadas, novaOperacaoPortuaria]);
    setCidadePortoOperacao('');
    setIdPortoOperacao(-1);
  };

  const adicionarEstrutura = () => {
    if (!ufEstruturaFora || !municipioEstruturaFora) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, preencha todos os campos para adicionar uma estrutura.',
      });
      return;
    }

    if (municipioEstruturaFora && municipioEstruturaFora.toString() === context?.municipio?.codigoIBGE?.toString()) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'A estrutura selecionada não pode estar no município produtor.',
      });
      return;
    }

    const novaEstrutura: IEstrutura = {
      uf: ufEstruturaFora,
      codigoIBGE: municipioEstruturaFora,
      nome: municipioNomeEstruturaFora,
    };

    const jaExiste = estruturasAdicionadas.some(
      (e) =>
        e?.uf?.toString() === novaEstrutura?.uf?.toString() &&
        e?.codigoIBGE?.toString() === novaEstrutura?.codigoIBGE?.toString()
    );
    if (jaExiste) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Esta estrutura já foi adicionada.',
      });
      return;
    }

    setEstruturasAdicionadas([...estruturasAdicionadas, novaEstrutura]);
    setUfEstruturaFora('');
    setMunicipioEstruturaFora(-1);
  };

  const excluirItem = <T,>(index: number, items: T[], setItems: React.Dispatch<React.SetStateAction<T[]>>) => {
    const novosItems = items.filter((_, i) => i !== index);
    setItems(novosItems);
  };
  const { data: canHaveDutovia, isLoading } = useQuery<boolean, IErrorWithSentryId>({
    queryKey: ['getCanHaveDutovia', guiaRecolhimentoId],
    queryFn: () => getCanHaveDutovia(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  return (
    <div className={styles.container}>
      <div className={styles.titulo}>
        <span>Modalidade de Afetação</span>
      </div>
      <div className={styles.divider}>
        <BrDivider />
      </div>
      {isLoading ? (
        <BrLoading large />
      ) : (
        <div className={styles.campos}>
          <div data-tooltip-id='switch-ferrovia'>
            <AnmSwitch
              label='O bem mineral foi transportado por ferrovia?'
              value={hasFerrovia}
              setValue={setHasFerrovia}
              disable={hasDutovia}
            />
          </div>
          <Tooltip
            opacity={1}
            id='switch-ferrovia'
            place='bottom'
            className={styles.tooltip}
            variant='warning'
            disableTooltip={() => !hasDutovia}
          >
            <div className={styles.tooltipText}>
              Não é possível possuir dutovia e ferrovia ao mesmo tempo em um mesmo NCM. Por favor desabilite dutovia
              caso deseje preencher ferrovias.
            </div>
          </Tooltip>
          {hasFerrovia && (
            <form autoComplete='off'>
              <div className={styles.inputs}>
                <Row>
                  <Col>
                    <AnmSelectQuery
                      label='Ferrovia'
                      value={ferrovia}
                      setValue={setFerrovia}
                      functionQuery={() => getFerrovias()}
                      keyQuery={['getFerroviasQuery']}
                      formatResult={(result: IFerroviasLista[]) =>
                        result.map((ferrovia: IFerroviasLista) => ({
                          label: ferrovia.nome + ' - ' + ferrovia.sigla,
                          value: ferrovia.id,
                        }))
                      }
                      setLabel={setFerroviaNome}
                      setRetornoQuery={setFerroviaData}
                    />
                  </Col>
                  <Col>
                    <BrSelect
                      label='UF Origem'
                      options={estadosUfFerroviaOptions}
                      value={ufOrigemFerrovia}
                      onChange={(value: string) => {
                        setUfOrigemFerrovia(value);
                        setMunicipioNomeOrigemFerrovia('');
                        setMunicipioOrigemFerrovia(-1);
                      }}
                      key={ufOrigemFerrovia}
                      placeholder='Selecionar'
                      disabled={ferrovia === -1}
                    />
                  </Col>
                  <Col>
                    <AnmSelectQuery
                      label='Município de Origem'
                      value={municipioOrigemFerrovia}
                      setValue={setMunicipioOrigemFerrovia}
                      functionQuery={() => getMunicipiosPorFerroviaEUf(ferrovia, ufOrigemFerrovia)}
                      keyQuery={['getMunicipiosPorUfQuery', ufOrigemFerrovia]}
                      formatResult={(result: IMunicipio[]) =>
                        result.map((municipio: IMunicipio) => ({
                          label: municipio.nome,
                          value: municipio.codigoIBGE,
                        }))
                      }
                      enableQuery={!!ufOrigemFerrovia}
                      isDisabled={!ufOrigemFerrovia}
                      setLabel={setMunicipioNomeOrigemFerrovia}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <BrSelect
                      label='UF Destino'
                      options={estadosUfFerroviaOptions}
                      value={ufDestinoFerrovia}
                      onChange={(value: string) => {
                        setUfDestinoFerrovia(value);
                        setMunicipioNomeDestinoFerrovia('');
                        setMunicipioDestinoFerrovia(-1);
                      }}
                      key={ufDestinoFerrovia}
                      placeholder='Selecionar'
                      disabled={ferrovia === -1}
                    />
                  </Col>
                  <Col>
                    <AnmSelectQuery
                      label='Município de Destino'
                      value={municipioDestinoFerrovia}
                      setValue={setMunicipioDestinoFerrovia}
                      functionQuery={() => getMunicipiosPorFerroviaEUf(ferrovia, ufDestinoFerrovia)}
                      keyQuery={['getMunicipiosPorUfQuery', ufDestinoFerrovia]}
                      formatResult={(result: IMunicipio[]) =>
                        result.map((municipio: IMunicipio) => ({
                          label: municipio.nome,
                          value: municipio.codigoIBGE,
                        }))
                      }
                      enableQuery={!!ufDestinoFerrovia}
                      isDisabled={!ufDestinoFerrovia}
                      setLabel={setMunicipioNomeDestinoFerrovia}
                    />
                  </Col>
                  <Col alignSelf='center'>
                    <BrButton circle secondary icon='plus' onClick={() => adicionarFerrovia()} />
                  </Col>
                </Row>
              </div>
              <div className={styles.tabela}>
                <Table showTopBar={false} showPageSelector={false} showPagination={false} density='small'>
                  <thead>
                    <tr>
                      <th>Ferrovia</th>
                      <th>UF Origem</th>
                      <th>Município de Origem</th>
                      <th>UF Destino</th>
                      <th>Município de Destino</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ferroviasAdicionadas.length === 0 && (
                      <tr>
                        <td colSpan={6}>Ainda não foram adicionadas ferrovias</td>
                      </tr>
                    )}
                    {ferroviasAdicionadas.map((ferroviaAdicionada, index) => (
                      <tr key={index}>
                        <td>{ferroviaAdicionada.nomeFerrovia}</td>
                        <td>{ferroviaAdicionada.origem.uf}</td>
                        <td>{ferroviaAdicionada.origem.nome}</td>
                        <td>{ferroviaAdicionada.destino.uf}</td>
                        <td>{ferroviaAdicionada.destino.nome}</td>
                        <td>
                          <BrButton
                            circle
                            secondary
                            icon='trash'
                            onClick={() => excluirItem(index, ferroviasAdicionadas, setFerroviasAdicionadas)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </form>
          )}
          {canHaveDutovia && (
            <>
              <BrDivider />
              <div data-tooltip-id='switch-dutovia'>
                <AnmSwitch
                  label='O bem mineral foi transportado por dutovia?'
                  value={hasDutovia}
                  setValue={setHasDutovia}
                  disable={hasFerrovia}
                />
              </div>
              <Tooltip
                opacity={1}
                id='switch-dutovia'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => !hasFerrovia}
              >
                <div className={styles.tooltipText}>
                  Não é possível possuir dutovia e ferrovia ao mesmo tempo em um mesmo NCM. Por favor desabilite
                  ferrovias caso deseje preencher dutovias.
                </div>
              </Tooltip>
            </>
          )}

          <BrDivider />
          <AnmSwitch
            label='Houve operações portuárias (embarque ou desembarque) vinculadas ao bem mineral?'
            value={hasOperacoesPortuarias}
            setValue={setHasOperacoesPortuarias}
          />
          {hasOperacoesPortuarias && (
            <>
              <div className={styles.inputs}>
                <Row>
                  <Col>
                    <AnmSelectQuery
                      label='Porto'
                      value={idPortoOperacao}
                      setValue={setIdPortoOperacao}
                      functionQuery={() => getPortos()}
                      keyQuery={['getPortosQuery']}
                      formatResult={(result: IPortosLista[]) =>
                        result.map((porto: IPortosLista) => ({
                          label: porto.uf + ' - ' + porto.cidade,
                          value: porto.id,
                        }))
                      }
                      setLabel={setCidadePortoOperacao}
                    />
                  </Col>
                  <Col alignSelf='center'>
                    <BrButton circle secondary icon='plus' onClick={() => adicionarOperacaoPortuaria()} />
                  </Col>
                </Row>
              </div>
              <div className={styles.tabela}>
                <Table showTopBar={false} showPageSelector={false} showPagination={false}>
                  <thead>
                    <tr>
                      <th>Porto</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operacoesPortuariasAdicionadas.length === 0 && (
                      <tr>
                        <td colSpan={6}>Ainda não foram adicionadas operações portuárias</td>
                      </tr>
                    )}
                    {operacoesPortuariasAdicionadas.map((operacaoPortuariaAdicionada, index) => (
                      <tr key={index}>
                        <td>{operacaoPortuariaAdicionada.municipio}</td>
                        <td>
                          <BrButton
                            circle
                            secondary
                            icon='trash'
                            onClick={() =>
                              excluirItem(index, operacoesPortuariasAdicionadas, setOperacoesPortuariasAdicionadas)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
          <BrDivider />
          <AnmSwitch
            label='Utilizou estruturas de mineração fora do município produtor?'
            value={hasEstruturasFora}
            setValue={setHasEstruturasFora}
          />
          {hasEstruturasFora && (
            <>
              <div className={styles.inputs}>
                <Row>
                  <Col>
                    <BrSelect
                      label='UF das Estruturas'
                      options={estadosUFOptions}
                      value={ufEstruturaFora}
                      onChange={(value: string) => {
                        setUfEstruturaFora(value);
                        setMunicipioNomeEstruturaFora('');
                        setMunicipioEstruturaFora(-1);
                      }}
                      key={ufEstruturaFora}
                      placeholder='Selecionar'
                    />
                  </Col>
                  <Col>
                    <AnmSelectQuery
                      label='Cidade das Estruturas'
                      value={municipioEstruturaFora}
                      setValue={setMunicipioEstruturaFora}
                      functionQuery={() => getMunicipiosPorUf(ufEstruturaFora)}
                      keyQuery={['getMunicipiosPorUfQuery', ufEstruturaFora]}
                      formatResult={(result: IMunicipio[]) =>
                        result.map((municipio: IMunicipio) => ({
                          label: municipio.nome,
                          value: municipio.codigoIBGE,
                        }))
                      }
                      enableQuery={!!ufEstruturaFora}
                      isDisabled={!ufEstruturaFora}
                      setLabel={setMunicipioNomeEstruturaFora}
                    />
                  </Col>
                  <Col alignSelf='center'>
                    <BrButton circle secondary icon='plus' onClick={() => adicionarEstrutura()} />
                  </Col>
                </Row>
              </div>
              <div className={styles.tabela}>
                <Table showTopBar={false} showPageSelector={false} showPagination={false}>
                  <thead>
                    <tr>
                      <th>UF das estruturas</th>
                      <th>Cidade das estruturas</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estruturasAdicionadas.length === 0 && (
                      <tr>
                        <td colSpan={6}>Ainda não foram adicionadas estruturas</td>
                      </tr>
                    )}
                    {estruturasAdicionadas.map((estruturaAdicionada, index) => (
                      <tr key={index}>
                        <td>{estruturaAdicionada.uf}</td>
                        <td>{estruturaAdicionada.nome}</td>
                        <td>
                          <BrButton
                            circle
                            secondary
                            icon='trash'
                            onClick={() => excluirItem(index, estruturasAdicionadas, setEstruturasAdicionadas)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ModalidadeAfetacao;
